import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { store } from '@/redux/store'

export function cn (...inputs) {
  return twMerge(clsx(inputs))
}

export const trunCateText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...'
  }
  return text
}

export function calculatePriceWithVAT (price, vatPercentage) {
  const basePrice = typeof price === 'number' ? price : 0
  const vat = typeof vatPercentage === 'number' ? vatPercentage : 0

  const vatAmount = (basePrice * vat) / 100
  const total = basePrice + vatAmount

  return total
}

function formatFieldName (field) {
  const withSpaces = field.replace(/_/g, ' ')
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1)
}

export const getFirstErrorMessage = form => {
  const errors = form.formState.errors
  const firstKey = Object.keys(errors)[0]
  if (!firstKey) return null

  const error = errors[firstKey]
  let message = null

  if (typeof error === 'string') {
    message = error
  } else if (error?.message) {
    message = error.message
  } else if (error?.type === 'required') {
    const fieldName = formatFieldName(firstKey)
    message = `${fieldName} is required`
  } else if (Array.isArray(error)) {
    message = error[0]?.message || error[0]
  }

  return message
}

export const playSound = soundFile => {
  const state = store.getState().utility?.soundNotification
  const { posSound = true, volume } = state || {}

  if (!posSound) return

  const audio = new Audio(soundFile)
  audio.volume = volume
  audio.play()
}

export const formatLabel = s => {
  return s?.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
}

const currencyLocaleMap = {
  GBP: 'en-GB',
  USD: 'en-US',
  EUR: 'de-DE',
  BDT: 'bn-BD'
}

export const formatCurrency = (value, currency = 'GBP') => {
  const locale = currencyLocaleMap[currency] || 'en-GB'
  const number = Number(value)

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(isNaN(number) ? 0 : number)
}

// Format date helper
export const formatDate = dateString => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return `${date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })} ${date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })}`
  } catch (error) {
    return 'N/A'
  }
}

export const formatCountdownDisplay = (seconds) => {
  if (seconds <= 0) return ''
  
  if (seconds < 60) {
    return `${seconds}s`
  }
  
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * Safely resolves the customer name from an order or reservation object.
 * Priority:
 * 1. order.customer?.full_name
 * 2. order.customer_full_name
 * 3. order.customer?.name
 * 4. order.customer_name
 * 5. order.customer_info?.customer_name
 * 6. fallback (default: "Unknown")
 */
export function getOrderCustomerName(order, fallback = "Unknown") {
  if (!order) return fallback;
  const name =
    order?.customer?.full_name ||
    order?.customer_full_name ||
    order?.customer?.name ||
    order?.customer_name ||
    order?.customer_info?.customer_name;
  return name && String(name).trim() ? String(name).trim() : fallback;
}

/**
 * Safely resolves the customer phone number from an order or reservation object.
 */
export function getOrderCustomerPhone(order, fallback = "") {
  if (!order) return fallback;
  const phone =
    order?.customer?.number ||
    order?.customer_number ||
    order?.customer_info?.phone_number ||
    order?.customer?.phone ||
    order?.phone ||
    order?.mobile;
  return phone && String(phone).trim() ? String(phone).trim() : fallback;
}

/**
 * Extracts a normalized customer object from an order even if order.customer is null.
 */
export function getNormalizedCustomer(order) {
  if (!order) return null;
  if (order.customer) {
    return {
      ...order.customer,
      id: order.customer.id ?? order.customer_id ?? null,
      full_name: order.customer.full_name || order.customer_full_name || order.customer.name || order.customer_name || "",
      number: order.customer.number || order.customer_number || "",
      postal_code: order.customer.postal_code || order.customer_postal_code || "",
    };
  }
  if (order.customer_full_name || order.customer_number || order.customer_id || order.customer_name) {
    return {
      id: order.customer_id || null,
      full_name: order.customer_full_name || order.customer_name || "",
      number: order.customer_number || "",
      postal_code: order.customer_postal_code || "",
    };
  }
  return null;
}

